import {
  ReviewLockerQuery,
  useProReviewUpdateByPkMutation,
  useReviewLockerQuery,
} from 'generated/graphql';
import { useCallback } from 'react';
import { ToastError, ToastSuccess } from 'utils';

export type ReviewLocker = NonNullable<
  ReviewLockerQuery['proReviewByPk']
>['locker'];

export const useReviewLocker = (proReviewId: number) => {
  const [updateByPkProReview, { loading: loadingMutation }] =
    useProReviewUpdateByPkMutation();

  const { data, loading, refetch } = useReviewLockerQuery({
    variables: {
      proReviewId,
    },
    fetchPolicy: 'network-only',
  });

  const locker = data?.proReviewByPk?.locker;
  const isLocked = !!locker;

  const checkLocker = useCallback(async (): Promise<ReviewLocker> => {
    const proReview = await refetch();
    return proReview.data.proReviewByPk?.locker;
  }, [refetch]);

  const lock = useCallback(
    async (bilikPersonId: number) => {
      const proReview = await refetch();
      if (proReview.data.proReviewByPk?.lockerId) {
        ToastError('Erreur', 'Avis déjà verrouillé');
        return;
      }

      await updateByPkProReview({
        variables: {
          proReviewId,
          lockerDate: new Date(),
          lockerId: bilikPersonId,
        },
      }).catch((error) => {
        ToastError('Erreur', 'Erreur lors de la demande de verrouillage');
        throw error;
      });

      await refetch();
      ToastSuccess('Succès', 'Avis verrouillé');
    },
    [proReviewId],
  );

  const unlock = useCallback(async () => {
    const proReview = await refetch();
    if (!proReview.data.proReviewByPk?.lockerId) return;

    await updateByPkProReview({
      variables: {
        proReviewId,
        lockerDate: null,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore codegen erases input nullability (maybeValue: 'T')
        lockerId: null,
      },
    }).catch((error) => {
      ToastError('Erreur', 'Erreur lors de la demande de déverrouillage');
      throw error;
    });

    await refetch();
    ToastSuccess('Succès', 'Avis déverrouillé');
  }, [proReviewId]);

  return {
    locker,
    isLocked,
    lock,
    unlock,
    checkLocker,
    loading: loading || loadingMutation,
  };
};
